import { useMemo } from 'react';
import { format } from 'date-fns';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import { pie3dPlugin } from '@/components/business-reports/pie3d-chart-plugin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DashboardAnalyticsResponse, DashboardGranularity } from '@/api/businessReports';
import { cn } from '@/lib/utils';
import { formatLeadSourceLabel } from '@/lib/leadSources';
import { IndianRupee, TrendingUp, Activity, Scissors, Users, UserCheck, PieChart as PieChartIcon, Star, MessageSquareHeart, MapPin } from 'lucide-react';

const EXPENSE_CATEGORY_ACRONYMS = new Set(['OPD', 'EMI', 'IPD', 'TDS', 'LAB', 'OT']);

function formatExpenseCategoryLabel(category: string): string {
  switch (category) {
    case 'ONLINE_MARKETING':
      return 'Online marketing';
    case 'OFFLINE_MARKETING':
      return 'Offline marketing';
    case 'LOCUM':
      return 'Locum';
    case 'SURGEON_PAYOUT':
      return 'Surgeon payout';
    case 'ANESTHETIST_PAYOUT':
      return 'Anesthetist payout';
    case 'SURGENIX_PAYOUT':
      return 'Surgenix (legacy)';
    case 'CARELANE_PAYOUT':
      return 'Carelane (legacy)';
    case 'HEXA_PAYOUT':
      return 'Hexa (legacy)';
    case 'PRACTO_PAYOUT':
      return 'Practo (legacy)';
    case 'B2B_PARTNER_PAYOUT':
      return 'B2B Partner Payout';
    case 'CUSTOM_PAYOUT':
      return 'Custom payout';
    case 'EMI_FINANCE_CHARGES':
      return 'EMI/Reimbursement Cost (Finance Partner Payout)';
    case 'PRACTO_CENTRAL':
      return 'Practo';
    case 'CAPEX_HOSPITAL':
      return 'Capex hospital';
    case 'REFERRAL':
      return 'Referral';
    case 'SECURITY_DEPOSIT':
      return 'Security deposit';
    case 'FUNDING_EXPENSES':
      return 'Funding Expenses';
    case 'TEAM_ENGAGEMENT':
      return 'Team Engagement';
    case 'CLIENT_ENGAGEMENT':
      return 'Client Engagement';
    case 'BIOMEDICAL_WASTE':
      return 'Biomedical Waste';
    case 'SALARY_UNIT':
      return 'Salary (Unit)';
    case 'SALARY_CENTRAL':
      return 'Salary (Central)';
    case 'OTHER_EXPENSES':
    case 'OTHER_CENTRAL':
      return 'Other Expenses';
    case 'OTHER_EXPENSES_CAPEX':
    case 'OTHER_CENTRAL_CAPEX':
      return 'Other Expenses — Capex';
    case 'OTHER_EXPENSES_SECURITY':
    case 'OTHER_CENTRAL_SECURITY':
      return 'Other Expenses — Security Deposit';
    case 'OTHER_EXPENSES_REMAINING':
    case 'OTHER_CENTRAL_REMAINING':
      return 'Other Expenses — Remaining';
    default:
      return category
        .split('_')
        .map((word) => {
          const upper = word.toUpperCase();
          if (EXPENSE_CATEGORY_ACRONYMS.has(upper)) return upper;
          const lower = word.toLowerCase();
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ');
  }
}

function formatExpenseHeader(row: { source: string; category: string; subCategory: string | null }): string {
  const label = formatExpenseCategoryLabel(row.category);
  const prefix = row.source === 'Central' ? 'Central' : 'IPD Patient';
  const sub = row.subCategory?.trim();
  if (sub) return `${prefix} — ${label} (${sub})`;
  return `${prefix} — ${label}`;
}

function formatBucketLabel(iso: string, granularity: DashboardGranularity): string {
  const d = new Date(iso);
  switch (granularity) {
    case 'year':
      return format(d, 'yyyy');
    case 'month':
      return format(d, 'MMM yyyy');
    case 'week':
      return `W/C ${format(d, 'dd MMM')}`;
    default:
      return format(d, 'dd MMM');
  }
}

function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return format(new Date(y, m - 1, 1), 'MMM yyyy');
}

const rupeeTooltip = {
  callbacks: {
    label: (ctx: { dataset: { label?: string }; parsed: { y?: number } }) => {
      const v = ctx.parsed.y ?? 0;
      return `${ctx.dataset.label ?? ''}: ₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    },
  },
};

const countTooltip = {
  callbacks: {
    label: (ctx: { dataset: { label?: string }; parsed: { y?: number } }) =>
      `${ctx.dataset.label ?? ''}: ${(ctx.parsed.y ?? 0).toLocaleString('en-IN')}`,
  },
};

const ARTIS_CHART = {
  gold: '#B07A3E',
  brown: '#6F553D',
  brownDark: '#2A1B11',
  slate: '#707B82',
  terracotta: '#A65D3F',
  sandGold: '#C4A574',
} as const;

const INVESTOR_REVENUE_LINE_COLORS = [
  ARTIS_CHART.gold,
  ARTIS_CHART.brown,
  ARTIS_CHART.terracotta,
  ARTIS_CHART.slate,
  ARTIS_CHART.brownDark,
  ARTIS_CHART.sandGold,
];

function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function investorLineBarOptions(investorMode: boolean) {
  if (!investorMode) return {};
  return {
    scales: {
      x: {
        ticks: { color: ARTIS_CHART.slate },
        grid: { color: withAlpha(ARTIS_CHART.brown, 0.1) },
      },
      y: {
        ticks: { color: ARTIS_CHART.slate },
        grid: { color: withAlpha(ARTIS_CHART.brown, 0.1) },
      },
    },
    plugins: {
      legend: {
        labels: { color: ARTIS_CHART.brownDark },
      },
    },
  };
}

const LEAD_SOURCE_PIE_COLORS = [
  ARTIS_CHART.gold,
  ARTIS_CHART.brown,
  ARTIS_CHART.terracotta,
  ARTIS_CHART.slate,
  '#2563EB',
  '#16A34A',
  '#7C3AED',
  '#EAB308',
  '#DC2626',
  '#0EA5E9',
  ARTIS_CHART.brownDark,
  ARTIS_CHART.sandGold,
];

ChartJS.register(ArcElement, pie3dPlugin);

interface BusinessReportsDashboardProps {
  data: DashboardAnalyticsResponse | null | undefined;
  isLoading: boolean;
  isError?: boolean;
  errorMessage?: string | null;
  granularity: DashboardGranularity;
  onGranularityChange: (g: DashboardGranularity) => void;
  investorMode?: boolean;
  /** Chain consolidated (all hospitals) — voice KPIs are pooled across units. */
  chainConsolidated?: boolean;
}

export function BusinessReportsDashboard({
  data,
  isLoading,
  isError,
  errorMessage,
  granularity,
  onGranularityChange,
  investorMode = false,
  chainConsolidated = false,
}: BusinessReportsDashboardProps) {
  const voiceScopeLabel = chainConsolidated ? 'all responses pooled' : 'hospital-scoped';
  const labels = useMemo(
    () => (data?.revenue.series ?? []).map((s) => formatBucketLabel(s.bucket, granularity)),
    [data?.revenue.series, granularity]
  );

  const totalRevenueChart = useMemo(() => {
    if (!data?.revenue.series.length) return null;
    const lineColor = investorMode ? ARTIS_CHART.gold : '#0F172A';
    const fillColor = investorMode ? withAlpha(ARTIS_CHART.gold, 0.18) : 'rgba(15,23,42,0.15)';
    return {
      labels,
      datasets: [
        {
          label: 'Total revenue',
          data: data.revenue.series.map((s) => s.total),
          borderColor: lineColor,
          backgroundColor: fillColor,
          tension: 0.25,
          fill: true,
        },
      ],
    };
  }, [data?.revenue.series, labels, investorMode]);

  const revenueBreakdownChart = useMemo(() => {
    if (!data?.revenue.series.length) return null;
    const rows = [
      { label: 'OPD', data: data.revenue.series.map((s) => s.opd), defaultColor: '#2563EB' },
      { label: 'Lab', data: data.revenue.series.map((s) => s.lab), defaultColor: '#F97316' },
      { label: 'Pharma', data: data.revenue.series.map((s) => s.pharma), defaultColor: '#16A34A' },
      {
        label: 'Additional Services',
        data: data.revenue.series.map((s) => s.additionalServices ?? 0),
        defaultColor: '#0D9488',
      },
      { label: 'IPD', data: data.revenue.series.map((s) => s.ipd), defaultColor: '#7C3AED' },
      { label: 'Day Care', data: data.revenue.series.map((s) => s.dc), defaultColor: '#EAB308' },
    ];
    return {
      labels,
      datasets: rows.map((row, index) => {
        const color = investorMode ? INVESTOR_REVENUE_LINE_COLORS[index] : row.defaultColor;
        return {
          label: row.label,
          data: row.data,
          borderColor: color,
          backgroundColor: withAlpha(color, 0.15),
          tension: 0.25,
        };
      }),
    };
  }, [data?.revenue.series, labels, investorMode]);

  const expenseChart = useMemo(() => {
    if (!data?.expenses.series.length) return null;
    const expLabels = data.expenses.series.map((s) => formatBucketLabel(s.bucket, granularity));
    const ipdColor = investorMode ? ARTIS_CHART.brown : '#DC2626';
    const centralColor = investorMode ? ARTIS_CHART.gold : '#0891B2';
    return {
      labels: expLabels,
      datasets: [
        {
          label: 'IPD Patient expenses',
          data: data.expenses.series.map((s) => s.ipdPatient),
          borderColor: ipdColor,
          backgroundColor: withAlpha(ipdColor, 0.2),
          tension: 0.25,
        },
        {
          label: 'Central expenses',
          data: data.expenses.series.map((s) => s.central),
          borderColor: centralColor,
          backgroundColor: withAlpha(centralColor, 0.2),
          tension: 0.25,
        },
      ],
    };
  }, [data?.expenses.series, granularity, investorMode]);

  const opdCountChart = useMemo(() => {
    if (!data?.opdCount.series.length) return null;
    const opdLabels = data.opdCount.series.map((s) => formatBucketLabel(s.bucket, granularity));
    const barColor = investorMode ? withAlpha(ARTIS_CHART.gold, 0.75) : 'rgba(37,99,235,0.65)';
    return {
      labels: opdLabels,
      datasets: [
        {
          label: 'OPD count',
          data: data.opdCount.series.map((s) => s.count),
          backgroundColor: barColor,
          borderRadius: 4,
        },
      ],
    };
  }, [data?.opdCount.series, granularity, investorMode]);

  const uniquePatientsChart = useMemo(() => {
    if (!data?.patientsBilled?.series.length) return null;
    const patientLabels = data.patientsBilled.series.map((s) =>
      formatBucketLabel(s.bucket, granularity)
    );
    const allColor = investorMode ? ARTIS_CHART.brown : '#0F766E';
    const plasticColor = investorMode ? ARTIS_CHART.gold : '#7C3AED';
    return {
      labels: patientLabels,
      datasets: [
        {
          label: 'All unique',
          data: data.patientsBilled.series.map((s) => s.unique),
          borderColor: allColor,
          backgroundColor: withAlpha(allColor, 0.12),
          tension: 0.25,
          fill: false,
        },
        {
          label: 'Plastic surgery',
          data: data.patientsBilled.series.map((s) => s.uniquePlastic ?? 0),
          borderColor: plasticColor,
          backgroundColor: withAlpha(plasticColor, 0.12),
          tension: 0.25,
          fill: false,
          borderDash: [6, 4],
        },
      ],
    };
  }, [data?.patientsBilled?.series, granularity, investorMode]);

  const repeatPatientsChart = useMemo(() => {
    if (!data?.patientsBilled?.series.length) return null;
    const patientLabels = data.patientsBilled.series.map((s) =>
      formatBucketLabel(s.bucket, granularity)
    );
    const allColor = investorMode ? ARTIS_CHART.gold : '#C2410C';
    const plasticColor = investorMode ? ARTIS_CHART.brown : '#7C3AED';
    return {
      labels: patientLabels,
      datasets: [
        {
          label: 'All repeat',
          data: data.patientsBilled.series.map((s) => s.repeat),
          borderColor: allColor,
          backgroundColor: withAlpha(allColor, 0.12),
          tension: 0.25,
          fill: false,
        },
        {
          label: 'Plastic surgery',
          data: data.patientsBilled.series.map((s) => s.repeatPlastic ?? 0),
          borderColor: plasticColor,
          backgroundColor: withAlpha(plasticColor, 0.12),
          tension: 0.25,
          fill: false,
          borderDash: [6, 4],
        },
      ],
    };
  }, [data?.patientsBilled?.series, granularity, investorMode]);

  const paretoChart = useMemo(() => {
    if (!data?.expensePareto.length) return null;
    const top = data.expensePareto.slice(0, 12);
    const shortLabels = top.map((r) => {
      const parts = r.header.split('::');
      const source = parts[0] === 'Central' ? 'Central' : 'IPD';
      const cat = formatExpenseCategoryLabel(parts[1] ?? '');
      const sub = parts[2]?.trim();
      const label = sub ? `${source} ${cat} (${sub})` : `${source} ${cat}`;
      return label.length > 28 ? `${label.slice(0, 26)}…` : label;
    });
    const barColor = investorMode ? withAlpha(ARTIS_CHART.gold, 0.65) : 'rgba(124,58,237,0.55)';
    const lineColor = investorMode ? ARTIS_CHART.terracotta : '#F97316';
    return {
      labels: shortLabels,
      datasets: [
        {
          label: 'Expense amount',
          data: top.map((r) => r.amount),
          backgroundColor: barColor,
          borderRadius: 4,
          yAxisID: 'y',
        },
        {
          type: 'line' as const,
          label: 'Cumulative %',
          data: top.map((r) => r.cumulativePercent),
          borderColor: lineColor,
          backgroundColor: 'transparent',
          tension: 0.25,
          yAxisID: 'y1',
        },
      ],
    };
  }, [data?.expensePareto, investorMode]);

  const revenueByLeadSourceChart = useMemo(() => {
    const rows = data?.revenueByLeadSource ?? [];
    if (!rows.length) return null;
    const labels = rows.map((r) => formatLeadSourceLabel(r.source));
    return {
      labels,
      datasets: [
        {
          data: rows.map((r) => r.revenue),
          backgroundColor: labels.map((_, i) =>
            investorMode
              ? withAlpha(LEAD_SOURCE_PIE_COLORS[i % LEAD_SOURCE_PIE_COLORS.length], 0.88)
              : LEAD_SOURCE_PIE_COLORS[i % LEAD_SOURCE_PIE_COLORS.length]
          ),
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverOffset: 6,
        },
      ],
    };
  }, [data?.revenueByLeadSource, investorMode]);

  const leadSourcePieOptions = useMemo(
    () => ({
      responsive: true,
      layout: { padding: { bottom: 18 } },
      plugins: {
        pie3d: { depth: 16 },
        legend: {
          position: 'bottom' as const,
          labels: {
            color: investorMode ? ARTIS_CHART.brownDark : undefined,
            boxWidth: 12,
            padding: 10,
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx: { label?: string; parsed?: number; dataIndex: number }) => {
              const v = Number(ctx.parsed ?? 0);
              const rows = data?.revenueByLeadSource ?? [];
              const row = rows[ctx.dataIndex];
              const pct = row?.sharePercent ?? 0;
              return `${ctx.label}: ₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })} (${pct}%)`;
            },
          },
        },
      },
    }),
    [data?.revenueByLeadSource, investorMode]
  );

  const leadSourceMomChart = useMemo(() => {
    const mom = data?.revenueByLeadSourceMom;
    if (!mom?.series.length || !mom.monthLabels.length) return null;
    const monthCount = mom.monthLabels.length;
    const monthTotals = Array.from({ length: monthCount }, (_, i) =>
      mom.series.reduce((sum, row) => sum + (row.revenues[i] ?? 0), 0)
    );
    return {
      labels: mom.monthLabels,
      datasets: mom.series.map((row, index) => {
        const color = LEAD_SOURCE_PIE_COLORS[index % LEAD_SOURCE_PIE_COLORS.length];
        const sharePercents = row.revenues.map((rev, i) => {
          const total = monthTotals[i] ?? 0;
          return total > 0 ? (rev / total) * 100 : 0;
        });
        return {
          label: formatLeadSourceLabel(row.source),
          data: sharePercents,
          rawRevenues: row.revenues,
          backgroundColor: withAlpha(color, investorMode ? 0.88 : 0.9),
          borderColor: withAlpha(color, 0.95),
          borderWidth: 1,
          stack: 'leadSource',
        };
      }),
    };
  }, [data?.revenueByLeadSourceMom, investorMode]);

  const revenueMetricCharts = useMemo(() => {
    if (!data?.revenue.series.length) return [];
    const metrics = [
      { key: 'opd' as const, label: 'OPD revenue', color: '#2563EB' },
      { key: 'lab' as const, label: 'Lab revenue', color: '#F97316' },
      { key: 'pharma' as const, label: 'Pharma revenue', color: '#16A34A' },
      { key: 'additionalServices' as const, label: 'Additional Services revenue', color: '#0D9488' },
      { key: 'ipd' as const, label: 'IPD revenue', color: '#7C3AED' },
      { key: 'dc' as const, label: 'Day Care revenue', color: '#EAB308' },
    ];
    return metrics.map((m, index) => {
      const color = investorMode ? INVESTOR_REVENUE_LINE_COLORS[index] : m.color;
      return {
        ...m,
        color,
        chart: {
          labels,
          datasets: [
            {
              label: m.label,
              data: data.revenue.series.map((s) => s[m.key]),
              borderColor: color,
              backgroundColor: withAlpha(color, 0.2),
              tension: 0.25,
              fill: true,
            },
          ],
        },
      };
    });
  }, [data?.revenue.series, labels, investorMode]);

  if (isLoading) {
    return (
      <Card className="mb-6 print:hidden">
        <CardContent className="py-10 text-center text-muted-foreground">Loading dashboard charts…</CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="mb-6 print:hidden border-amber-200 bg-amber-50/40">
        <CardContent className="py-8 space-y-2 text-center">
          <p className="font-medium text-amber-900">Could not load analytics charts</p>
          <p className="text-sm text-amber-800/90">
            {errorMessage || 'The dashboard analytics API did not respond.'}
          </p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            If revenue tables below still load, the server may be missing the latest API build.
            Pull the latest backend code, run <span className="font-mono text-xs">npm run build</span>, then restart{' '}
            <span className="font-mono text-xs">thms-api</span>.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="mb-6 print:hidden">
        <CardContent className="py-10 text-center text-slate-500">Select a date range to view analytics.</CardContent>
      </Card>
    );
  }

  const metricTileClass = investorMode
    ? 'rounded-xl border border-artis-brown/15 bg-gradient-to-br from-white to-artis-cream p-3 shadow-sm'
    : 'rounded-lg border bg-muted/20 p-3';
  const brandedChartOpts = investorLineBarOptions(investorMode);
  const investorCardClass = investorMode ? 'border-artis-brown/10 shadow-sm' : '';
  const investorTitleClass = investorMode ? 'font-artis-serif text-artis-brown-dark' : '';

  return (
    <div className={cn('space-y-6 mb-6 print:hidden', investorMode && 'font-artis-sans')}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={cn('text-lg font-semibold', investorMode ? 'font-artis-serif text-artis-brown-dark' : 'text-slate-900')}>
            Analytics dashboard
          </h2>
          <p className={cn('text-sm', investorMode ? 'text-artis-slate' : 'text-slate-500')}>
            Charts show data from 1 Apr 2026 through the end of your selected period. Summary tiles use the
            filter range (IPD patient &amp; central expenses by service month).
          </p>
        </div>
        <Select value={granularity} onValueChange={(v) => onGranularityChange(v as DashboardGranularity)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Granularity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Daily</SelectItem>
            <SelectItem value="week">Weekly</SelectItem>
            <SelectItem value="month">Monthly</SelectItem>
            <SelectItem value="year">Yearly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
        {[
          { label: 'Total revenue', value: data.revenue.totals.total, money: true },
          { label: 'OPD', value: data.revenue.totals.opd, money: true },
          { label: 'Lab', value: data.revenue.totals.lab, money: true },
          { label: 'Pharma', value: data.revenue.totals.pharma, money: true },
          { label: 'Additional Services', value: data.revenue.totals.additionalServices ?? 0, money: true },
          { label: 'IPD', value: data.revenue.totals.ipd, money: true },
          { label: 'Day Care', value: data.revenue.totals.dc, money: true },
          { label: 'IPD patient exp.', value: data.expenses.totals.ipdPatient, money: true },
          { label: 'Central exp.', value: data.expenses.totals.central, money: true },
        ].map((t) => (
          <div key={t.label} className={metricTileClass}>
            <p className={cn('text-xs', investorMode ? 'text-artis-slate' : 'text-slate-500')}>{t.label}</p>
            <p className={cn('text-base font-semibold tabular-nums mt-1', investorMode ? 'text-artis-brown-dark' : 'text-slate-900')}>
              {t.money
                ? `₹${t.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                : t.value.toLocaleString('en-IN')}
            </p>
          </div>
        ))}
      </div>

      {(data.patientsBilled?.totals != null || data.customerVoice || data.gmbRating) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {data.patientsBilled?.totals != null && (
            <>
              <div className={metricTileClass}>
                <p className={cn('text-xs', investorMode ? 'text-artis-slate' : 'text-slate-500')}>Unique patients billed</p>
                <p className={cn('text-base font-semibold tabular-nums mt-1', investorMode ? 'text-artis-brown-dark' : 'text-slate-900')}>
                  {data.patientsBilled.totals.unique.toLocaleString('en-IN')}
                </p>
                <p className={cn('text-[11px] mt-1', investorMode ? 'text-artis-slate' : 'text-slate-500')}>
                  In selected filter range
                  {data.patientsBilled.totals.uniquePlastic != null
                    ? ` · plastic ${data.patientsBilled.totals.uniquePlastic.toLocaleString('en-IN')}`
                    : ''}
                </p>
              </div>
              <div className={metricTileClass}>
                <p className={cn('text-xs', investorMode ? 'text-artis-slate' : 'text-slate-500')}>Repeat patients</p>
                <p className={cn('text-base font-semibold tabular-nums mt-1', investorMode ? 'text-artis-brown-dark' : 'text-slate-900')}>
                  {data.patientsBilled.totals.repeat.toLocaleString('en-IN')}
                </p>
                <p className={cn('text-[11px] mt-1', investorMode ? 'text-artis-slate' : 'text-slate-500')}>
                  ≥2 billings, ≥20 days apart
                  {data.patientsBilled.totals.repeatPlastic != null
                    ? ` · plastic ${data.patientsBilled.totals.repeatPlastic.toLocaleString('en-IN')}`
                    : ''}
                </p>
              </div>
            </>
          )}
          <div
            className={cn(
              metricTileClass,
              investorMode
                ? 'ring-2 ring-artis-gold/50 border-artis-gold/40 bg-gradient-to-br from-amber-50/90 to-artis-cream'
                : 'ring-2 ring-teal-500/40 border-teal-300 bg-gradient-to-br from-teal-50 to-emerald-50/80 shadow-sm'
            )}
          >
            <p
              className={cn(
                'text-xs font-medium flex items-center gap-1',
                investorMode ? 'text-artis-brown-dark' : 'text-teal-800'
              )}
            >
              <Star className={cn('h-3.5 w-3.5', investorMode ? 'text-artis-gold' : 'text-teal-600')} />
              NPS
            </p>
            <p
              className={cn(
                'text-2xl font-bold tabular-nums mt-1 tracking-tight',
                investorMode ? 'text-artis-brown-dark' : 'text-teal-900'
              )}
            >
              {data.customerVoice?.npsAverage != null
                ? `${data.customerVoice.npsAverage > 0 ? '+' : ''}${Math.round(data.customerVoice.npsAverage)}`
                : '—'}
            </p>
            <p className={cn('text-[11px] mt-1', investorMode ? 'text-artis-slate' : 'text-teal-700/80')}>
              {`% Promoters − % Detractors · cumulative · ${voiceScopeLabel}`}
              {data.customerVoice?.npsCount != null && data.customerVoice.npsCount > 0
                ? ` · ${data.customerVoice.npsCount} responses`
                : ''}
            </p>
          </div>
          <div
            className={cn(
              metricTileClass,
              investorMode
                ? 'ring-2 ring-artis-gold/50 border-artis-gold/40 bg-gradient-to-br from-amber-50/90 to-artis-cream'
                : 'ring-2 ring-amber-500/40 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50/80 shadow-sm'
            )}
          >
            <p
              className={cn(
                'text-xs font-medium flex items-center gap-1',
                investorMode ? 'text-artis-brown-dark' : 'text-amber-900'
              )}
            >
              <MapPin className={cn('h-3.5 w-3.5', investorMode ? 'text-artis-gold' : 'text-amber-600')} />
              GMB rating
            </p>
            <p
              className={cn(
                'text-2xl font-bold tabular-nums mt-1 tracking-tight',
                investorMode ? 'text-artis-brown-dark' : 'text-amber-950'
              )}
            >
              {data.gmbRating?.rating != null
                ? data.gmbRating.rating.toLocaleString('en-IN', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                : '—'}
            </p>
            <p className={cn('text-[11px] mt-1', investorMode ? 'text-artis-slate' : 'text-amber-800/80')}>
              Google listing
              {data.gmbRating?.reviewCount
                ? ` · ${data.gmbRating.reviewCount.toLocaleString('en-IN')} reviews`
                : ''}
              {data.gmbRating && data.gmbRating.locationCount > 1
                ? ` · ${data.gmbRating.locationCount} locations`
                : ''}
            </p>
            {data.gmbRating?.rating == null && data.gmbRating?.hint ? (
              <p
                className={cn(
                  'text-[10px] mt-1 leading-snug',
                  investorMode ? 'text-artis-slate' : 'text-amber-900/70'
                )}
                title={data.gmbRating.hint}
              >
                {data.gmbRating.hint.length > 90
                  ? `${data.gmbRating.hint.slice(0, 88)}…`
                  : data.gmbRating.hint}
              </p>
            ) : null}
          </div>
          <div
            className={cn(
              metricTileClass,
              investorMode
                ? 'ring-2 ring-artis-gold/50 border-artis-gold/40 bg-gradient-to-br from-amber-50/90 to-artis-cream'
                : 'ring-2 ring-indigo-500/40 border-indigo-300 bg-gradient-to-br from-indigo-50 to-violet-50/80 shadow-sm'
            )}
          >
            <p
              className={cn(
                'text-xs font-medium flex items-center gap-1',
                investorMode ? 'text-artis-brown-dark' : 'text-indigo-800'
              )}
            >
              <MessageSquareHeart className={cn('h-3.5 w-3.5', investorMode ? 'text-artis-gold' : 'text-indigo-600')} />
              Doctor feedback avg
            </p>
            <p
              className={cn(
                'text-2xl font-bold tabular-nums mt-1 tracking-tight',
                investorMode ? 'text-artis-brown-dark' : 'text-indigo-900'
              )}
            >
              {data.customerVoice?.doctorFeedbackAverage != null
                ? data.customerVoice.doctorFeedbackAverage.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : '—'}
            </p>
            <p className={cn('text-[11px] mt-1', investorMode ? 'text-artis-slate' : 'text-indigo-700/80')}>
              {`OPD WhatsApp · 1–5 · cumulative · ${voiceScopeLabel}`}
              {data.customerVoice?.doctorFeedbackCount != null && data.customerVoice.doctorFeedbackCount > 0
                ? ` · ${data.customerVoice.doctorFeedbackCount} responses`
                : ''}
            </p>
          </div>
        </div>
      )}

      <Card className={cn(investorMode && 'border-artis-brown/10 shadow-sm')}>
        <CardHeader className="pb-2">
          <CardTitle className={cn('flex items-center gap-2 text-base', investorMode && 'font-artis-serif text-artis-brown-dark')}>
            <IndianRupee className={cn('w-4 h-4', investorMode ? 'text-artis-gold' : 'text-emerald-600')} />
            Total revenue trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          {totalRevenueChart ? (
            <Line
              data={totalRevenueChart}
              options={{ responsive: true, plugins: { legend: { display: false }, tooltip: rupeeTooltip }, ...brandedChartOpts }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No revenue data for this period.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={investorCardClass}>
          <CardHeader className="pb-2">
            <CardTitle className={cn('flex items-center gap-2 text-base', investorTitleClass)}>
              <Users className={cn('w-4 h-4', investorMode ? 'text-artis-brown' : 'text-teal-700')} />
              Unique patients billed
              {data.patientsBilled?.totals != null && (
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  (period {data.patientsBilled.totals.unique.toLocaleString('en-IN')}
                  {data.patientsBilled.totals.uniquePlastic != null
                    ? ` · plastic ${data.patientsBilled.totals.uniquePlastic.toLocaleString('en-IN')}`
                    : ''}
                  )
                </span>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              Distinct patients with an OPD/Lab/Pharma/Day Care bill or IPD discharge in each interval.
              Plastic surgery line uses visits with plastic/aesthetic doctors (same matching as Plastic Surgery reports).
            </p>
          </CardHeader>
          <CardContent>
            {uniquePatientsChart ? (
              <Line
                data={uniquePatientsChart}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' }, tooltip: countTooltip },
                  ...brandedChartOpts,
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No patient billing data for this period.</p>
            )}
          </CardContent>
        </Card>

        <Card className={investorCardClass}>
          <CardHeader className="pb-2">
            <CardTitle className={cn('flex items-center gap-2 text-base', investorTitleClass)}>
              <UserCheck className={cn('w-4 h-4', investorMode ? 'text-artis-gold' : 'text-orange-700')} />
              Repeat patients
              {data.patientsBilled?.totals != null && (
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  (period {data.patientsBilled.totals.repeat.toLocaleString('en-IN')}
                  {data.patientsBilled.totals.repeatPlastic != null
                    ? ` · plastic ${data.patientsBilled.totals.repeatPlastic.toLocaleString('en-IN')}`
                    : ''}
                  )
                </span>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              Patients billed in the interval who also have an earlier billing at least 20 days prior.
              Plastic surgery line applies the same rule within plastic-doctor visits.
            </p>
          </CardHeader>
          <CardContent>
            {repeatPatientsChart ? (
              <Line
                data={repeatPatientsChart}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' }, tooltip: countTooltip },
                  ...brandedChartOpts,
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No repeat patient data for this period.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className={investorCardClass}>
        <CardHeader className="pb-2">
          <CardTitle className={cn('flex items-center gap-2 text-base', investorTitleClass)}>
            <TrendingUp className={cn('w-4 h-4', investorMode ? 'text-artis-gold' : 'text-blue-600')} />
            Revenue by service type
          </CardTitle>
        </CardHeader>
        <CardContent>
          {revenueBreakdownChart ? (
            <Line
              data={revenueBreakdownChart}
              options={{
                responsive: true,
                plugins: { legend: { position: 'top' }, tooltip: rupeeTooltip },
                interaction: { mode: 'index', intersect: false },
                ...brandedChartOpts,
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No revenue breakdown data.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {revenueMetricCharts.map((m) => (
          <Card key={m.key} className={investorCardClass}>
            <CardHeader className="pb-2">
              <CardTitle className={cn('text-sm font-medium', investorTitleClass)}>{m.label}</CardTitle>
            </CardHeader>
            <CardContent className="h-[200px]">
              <Line
                data={m.chart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false }, tooltip: rupeeTooltip },
                  ...brandedChartOpts,
                }}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className={cn(investorMode && 'border-artis-brown/10 shadow-sm')}>
        <CardHeader className="pb-2">
          <CardTitle className={cn('flex items-center gap-2 text-base', investorMode && 'font-artis-serif text-artis-brown-dark')}>
            <PieChartIcon className={cn('w-4 h-4', investorMode ? 'text-artis-gold' : 'text-violet-600')} />
            Revenue by lead channel
          </CardTitle>
          <p className={cn('text-sm font-normal', investorMode ? 'text-artis-slate' : 'text-muted-foreground')}>
            Share of OPD, Lab, Pharma, Day Care, and IPD revenue by lead / registration source in the selected filter range.
          </p>
        </CardHeader>
        <CardContent>
          {revenueByLeadSourceChart || leadSourceMomChart ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className="min-h-[280px]">
                {revenueByLeadSourceChart ? (
                  <div className="mx-auto h-full max-w-lg pt-2 pb-4">
                    <Pie data={revenueByLeadSourceChart} options={leadSourcePieOptions} />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No revenue by lead channel for this period.</p>
                )}
              </div>
              <div className="min-h-[280px] flex flex-col">
                <p className={cn('text-sm font-medium mb-1', investorMode ? 'text-artis-brown-dark' : 'text-slate-700')}>
                  Month-on-month by lead source
                </p>
                <p className={cn('text-xs mb-2', investorMode ? 'text-artis-slate' : 'text-muted-foreground')}>
                  Stacked monthly mix from Apr 2026 through the filter end date — all sources with revenue in any month (pie uses the full filter range only).
                </p>
                {leadSourceMomChart ? (
                  <div className="flex-1 min-h-[240px]">
                    <Bar
                      data={leadSourceMomChart}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              color: investorMode ? ARTIS_CHART.brownDark : undefined,
                              boxWidth: 10,
                              padding: 8,
                              font: { size: 11 },
                            },
                          },
                          tooltip: {
                            mode: 'index',
                            intersect: false,
                            callbacks: {
                              label: (ctx) => {
                                const dataset = ctx.dataset as {
                                  label?: string;
                                  rawRevenues?: number[];
                                };
                                const pct = ctx.parsed.y ?? 0;
                                const raw = dataset.rawRevenues?.[ctx.dataIndex] ?? 0;
                                return `${dataset.label ?? ''}: ${pct.toFixed(1)}% (₹${raw.toLocaleString('en-IN', { maximumFractionDigits: 0 })})`;
                              },
                              footer: (items) => {
                                const total = items.reduce(
                                  (sum, item) =>
                                    sum +
                                    ((item.dataset as { rawRevenues?: number[] }).rawRevenues?.[item.dataIndex] ?? 0),
                                  0
                                );
                                return total > 0
                                  ? `Month total: ₹${total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                  : '';
                              },
                            },
                          },
                        },
                        interaction: { mode: 'index', intersect: false },
                        scales: {
                          x: {
                            stacked: true,
                            ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } },
                          },
                          y: {
                            stacked: true,
                            max: 100,
                            ticks: {
                              callback: (v) => `${v}%`,
                            },
                          },
                        },
                        ...brandedChartOpts,
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No monthly lead-source trend for this period.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No revenue by lead channel for this period.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={investorCardClass}>
          <CardHeader className="pb-2">
            <CardTitle className={cn('text-base', investorTitleClass)}>Expenses — IPD Patient vs Central</CardTitle>
          </CardHeader>
          <CardContent>
            {expenseChart ? (
              <Line
                data={expenseChart}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' }, tooltip: rupeeTooltip },
                  interaction: { mode: 'index', intersect: false },
                  ...brandedChartOpts,
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No expense data for this period.</p>
            )}
          </CardContent>
        </Card>

        <Card className={investorCardClass}>
          <CardHeader className="pb-2">
            <CardTitle className={cn('flex items-center gap-2 text-base', investorTitleClass)}>
              <Activity className={cn('w-4 h-4', investorMode ? 'text-artis-gold' : 'text-blue-600')} />
              OPD count
              <span className="text-sm font-normal text-muted-foreground ml-1">
                (total {data.opdCount.total.toLocaleString('en-IN')})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {opdCountChart ? (
              <Bar
                data={opdCountChart}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false }, tooltip: countTooltip },
                  ...brandedChartOpts,
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No OPD visits in this period.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={investorCardClass}>
          <CardHeader className="pb-2">
            <CardTitle className={cn('text-base', investorTitleClass)}>
              Top expense headers — MoM (
              {data.expenseMom.previousLabel ??
                formatMonthLabel(data.expenseMom.previousMonth)}{' '}
              →{' '}
              {data.expenseMom.currentLabel ?? formatMonthLabel(data.expenseMom.currentMonth)})
            </CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              Month-to-date by expense date — current month compared to the same calendar days in the prior month.
            </p>
          </CardHeader>
          <CardContent>
            {!data.expenseMom.rows.length ? (
              <p className="text-sm text-muted-foreground">No expense data for month-over-month comparison.</p>
            ) : (
              <div className="overflow-auto max-h-[360px] rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/50 border-b">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Header</th>
                      <th className="text-right px-3 py-2 font-medium">Previous</th>
                      <th className="text-right px-3 py-2 font-medium">Current</th>
                      <th className="text-right px-3 py-2 font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenseMom.rows.map((row) => (
                      <tr key={row.header} className="border-b last:border-b-0 hover:bg-muted/30">
                        <td className="px-3 py-2">{formatExpenseHeader(row)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">₹{row.previousAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-2 text-right tabular-nums">₹{row.currentAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${row.changePercent > 0 ? 'text-red-600' : row.changePercent < 0 ? 'text-green-600' : ''}`}>
                          {row.changePercent > 0 ? '+' : ''}{row.changePercent}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={investorCardClass}>
          <CardHeader className="pb-2">
            <CardTitle className={cn('text-base', investorTitleClass)}>Expense Pareto analysis</CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              Top expense categories by service month in the selected period (not expense date).
            </p>
          </CardHeader>
          <CardContent>
            {paretoChart ? (
              <Bar
                data={paretoChart as any}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' }, tooltip: rupeeTooltip },
                  scales: {
                    y: { type: 'linear', position: 'left', ticks: { callback: (v) => `₹${Number(v).toLocaleString('en-IN')}` } },
                    y1: {
                      type: 'linear',
                      position: 'right',
                      min: 0,
                      max: 100,
                      grid: { drawOnChartArea: false },
                      ticks: { callback: (v) => `${v}%` },
                    },
                  },
                  ...brandedChartOpts,
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No expenses to analyse.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className={investorCardClass}>
        <CardHeader className="pb-2">
          <CardTitle className={cn('flex items-center gap-2 text-base', investorTitleClass)}>
            <Scissors className={cn('w-4 h-4', investorMode ? 'text-artis-gold' : 'text-purple-600')} />
            IPD surgery revenue (by surgery name)
          </CardTitle>
          <p className="text-sm text-muted-foreground font-normal">
            Surgery line items from discharged IPD admissions in the selected period.
          </p>
        </CardHeader>
        <CardContent>
          {!data.surgeryRevenue.length ? (
            <p className="text-sm text-muted-foreground">No surgery revenue in this period.</p>
          ) : (
            <div className="overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Surgery name</th>
                    <th className="text-right px-3 py-2 font-medium">Admissions</th>
                    <th className="text-right px-3 py-2 font-medium">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.surgeryRevenue.map((row) => (
                    <tr key={row.surgeryName} className="border-b last:border-b-0 hover:bg-muted/30">
                      <td className="px-3 py-2">{row.surgeryName}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.admissionCount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">₹{row.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
